import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, CheckCircle2, Copy, Check, Smartphone, ShieldCheck } from "lucide-react";

const isMobile = () =>
  typeof navigator !== "undefined" && /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

export default function PaymentModal({ open, onOpenChange, onSuccess }) {
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState("pay"); // pay | success
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState("");
  const [copied, setCopied] = useState(false);
  const mobile = isMobile();

  useEffect(() => {
    if (open) {
      setStep("pay"); setReference(""); setCopied(false); setOrder(null);
      api.post("/payment/create-order").then(({ data }) => setOrder(data)).catch(() => toast.error("Could not start payment"));
    }
  }, [open]);

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(order.upi_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const openUpiApp = () => { if (order?.upi_link) window.location.href = order.upi_link; };

  const confirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payment/confirm", { order_id: order.order_id, reference: reference.trim() });
      setStep("success");
      setTimeout(() => { onOpenChange(false); onSuccess && onSuccess(data); }, 1700);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Could not record your payment"));
    } finally { setLoading(false); }
  };

  const amount = order?.amount_rupees ?? 29;

  return (
    <Dialog open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden gap-0" data-testid="payment-modal">
        {/* Brand header */}
        <div className="bg-ink text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-full bg-emerald-500 grid place-items-center font-display font-extrabold text-white">R</div>
              <div>
                <div className="font-display font-bold text-[15px] leading-none">ReKTR Campus Pass</div>
                <div className="label-mono text-[9px] text-slate-400 mt-1">Pay via UPI</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-display font-extrabold text-2xl">₹{amount}</div>
              <div className="text-[10px] text-slate-400">per month</div>
            </div>
          </div>
        </div>

        {step === "success" ? (
          <div className="p-8 flex flex-col items-center text-center gap-3" data-testid="payment-success">
            <div className="h-16 w-16 rounded-full bg-emerald-50 grid place-items-center"><CheckCircle2 size={40} className="text-emerald-500" /></div>
            <h3 className="font-display font-bold text-xl text-ink">Payment recorded</h3>
            <p className="text-sm text-slate-500">Your Campus Pass is active. The ReKTR team will verify your payment shortly.</p>
          </div>
        ) : !order ? (
          <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>
        ) : (
          <div className="p-6 space-y-5" data-testid="upi-pay-panel">
            {/* Payee row */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500 grid place-items-center text-white font-display font-extrabold">R</div>
              <div className="min-w-0">
                <div className="font-semibold text-ink leading-tight">{order.payee_name}</div>
                <div className="text-xs text-slate-500">Paying ₹{amount} · ReKTR Campus Pass</div>
              </div>
            </div>

            {mobile && (
              <Button onClick={openUpiApp} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="upi-open-app-btn">
                <Smartphone size={18} className="mr-2" /> Pay ₹{amount} with any UPI app
              </Button>
            )}

            {/* QR card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col items-center" data-testid="upi-qr-card">
              <div className="rounded-xl bg-white p-3 ring-1 ring-slate-100 shadow-sm">
                <QRCodeSVG value={order.upi_link} size={168} bgColor="#ffffff" fgColor="#0F172A" level="M" />
              </div>
              <p className="mt-3 text-sm text-slate-500">{mobile ? "…or scan with another phone" : "Scan to pay with any UPI app"}</p>
              <button onClick={copyUpi} className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-mono text-ink hover:bg-slate-50" data-testid="upi-id-copy">
                {order.upi_id}
                {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} className="text-slate-400" />}
              </button>
            </div>

            {/* Manual confirmation */}
            <div className="space-y-2">
              <Label>UPI transaction / reference ID <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 4183xxxxxxxx" data-testid="upi-reference-input" />
            </div>
            <Button onClick={confirm} disabled={loading} className="w-full h-11 bg-ink hover:bg-slate-800 text-white" data-testid="confirm-payment-btn">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "I've completed the payment"}
            </Button>
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
              <ShieldCheck size={13} className="text-emerald-500" /> Paid directly to ReKTR via UPI · verified manually
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
